import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, Layers, PanelsTopLeft, Sparkles, Wrench, HardHat, Truck, Phone, ShieldCheck, Clock, MapPin } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { COMPANY, SERVICES } from "@/lib/constants";
import LogoShowcase from "@/components/sections/LogoShowcase";
import Stats from "@/components/sections/Stats";
import MountainDivider from "@/components/site/MountainDivider";

const ICONS = { Building2, Layers, PanelsTopLeft, Sparkles, Wrench, HardHat, Truck };

const HERO_IMG =
    "https://images.pexels.com/photos/34874718/pexels-photo-34874718.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

export default function Home() {
    const reduce = useReducedMotion();
    useSeo({
        title: "Alpen Reinigung Innsbruck – Gebäude-, Fenster- & Fassadenreinigung Tirol",
        description:
            "Premium Reinigungsservice in Innsbruck und ganz Tirol: Gebäudereinigung, Fassadenreinigung, Fensterreinigung, Unterhaltsreinigung & Spezialreinigung. Schnelle Rückmeldung, faire Preise. ☎ +43 676 7092658.",
        path: "/",
    });

    return (
        <div>
            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="hero-section">
                <div className="hero-glow absolute inset-0 pointer-events-none" />
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-28 pb-20 sm:pb-24 lg:pb-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        <motion.div
                            initial={reduce ? false : { opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                            className="lg:col-span-7"
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(185,194,207,0.25)] bg-white/[0.03] text-xs font-mono text-[#C9D3E1]">
                                <span className="h-1.5 w-1.5 rounded-full bg-[#2EA8FF] animate-pulse" />
                                Innsbruck · Tirol · Österreich
                            </div>
                            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
                                Premium Reinigung in{" "}
                                <span className="text-chrome">Innsbruck</span>
                                <br />
                                <span className="text-[#7BD3FF]">sichtbar</span> besser.
                            </h1>
                            <p className="mt-5 text-base sm:text-lg text-[#C9D3E1] max-w-2xl leading-relaxed">
                                {COMPANY.tagline} Für Privat & Gewerbe. Persönlich,
                                zuverlässig und mit dem Anspruch, den Sie an einen
                                Premium-Partner haben.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <a
                                    href={`tel:${COMPANY.phoneTel}`}
                                    data-testid="hero-call-button"
                                    className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded-xl bg-[#2EA8FF] text-[#07090C] font-medium hover:bg-[#7BD3FF] transition-colors btn-glow"
                                >
                                    <Phone className="h-4 w-4" />
                                    Jetzt anrufen · {COMPANY.phone}
                                </a>
                                <Link
                                    to="/kontakt"
                                    data-testid="hero-quote-button"
                                    className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded-xl border border-white/[0.14] text-[#F4F7FB] hover:border-[rgba(123,211,255,0.55)] hover:bg-[rgba(46,168,255,0.06)] transition-colors"
                                >
                                    Angebot anfragen
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
                                <div className="flex items-center gap-2 text-sm text-[#C9D3E1]">
                                    <Clock className="h-4 w-4 text-[#7BD3FF]" />
                                    Schnelle Rückmeldung
                                </div>
                                <div className="flex items-center gap-2 text-sm text-[#C9D3E1]">
                                    <ShieldCheck className="h-4 w-4 text-[#7BD3FF]" />
                                    Diskret & pünktlich
                                </div>
                                <div className="flex items-center gap-2 text-sm text-[#C9D3E1]">
                                    <MapPin className="h-4 w-4 text-[#7BD3FF]" />
                                    Privat & Gewerbe
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={reduce ? false : { opacity: 0, y: 24, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
                            className="lg:col-span-5"
                        >
                            <div className="relative">
                                <div className="absolute -inset-3 rounded-[28px] bg-[rgba(46,168,255,0.08)] blur-2xl" aria-hidden="true" />
                                <div className="relative rounded-[24px] overflow-hidden border border-white/[0.10] card-elevated">
                                    <div className="aspect-[4/5] relative">
                                        <img src={HERO_IMG} alt="Professionelle Fensterreinigung an einer modernen Glasfassade" className="absolute inset-0 h-full w-full object-cover" loading="eager" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#07090C] via-[#07090C]/40 to-transparent" />
                                        <div className="absolute bottom-4 left-4 right-4">
                                            <div className="rounded-xl border border-white/[0.10] bg-[rgba(14,20,31,0.6)] backdrop-blur-md p-4">
                                                <p className="text-xs font-mono text-[#7BD3FF] uppercase tracking-wider">Heute angefragt</p>
                                                <p className="mt-1 text-sm text-white">Glasfassaden · Treppenhäuser · Photovoltaik</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <LogoShowcase />
            <MountainDivider />

            {/* Services Preview */}
            <section className="relative py-20 sm:py-24 lg:py-28">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                        <div className="max-w-2xl">
                            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#7BD3FF]">Leistungen</p>
                            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">Reinigung. Auf <span className="text-chrome">Premium-Niveau</span>.</h2>
                            <p className="mt-4 text-[#C9D3E1] text-base leading-relaxed">Fünf Kernbereiche – ein Anspruch: konsistente Qualität, faire Preise und überraschend einfache Abwicklung.</p>
                        </div>
                        <Link to="/leistungen" className="self-start inline-flex items-center gap-2 px-4 h-11 rounded-xl border border-white/[0.14] text-sm text-[#F4F7FB] hover:border-[rgba(123,211,255,0.55)] hover:bg-[rgba(46,168,255,0.06)] transition-colors">
                            Alle Leistungen ansehen
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-testid="services-grid">
                        {SERVICES.map((s, i) => {
                            const Icon = ICONS[s.icon];
                            return (
                                <motion.div
                                    key={s.slug}
                                    initial={reduce ? false : { opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.45, delay: i * 0.05 }}
                                    className="group"
                                >
                                    <Link
                                        to={`/leistungen/${s.slug}`}
                                        className="block h-full rounded-2xl border border-white/[0.10] bg-[rgba(18,27,42,0.55)] backdrop-blur-md p-6 hover:border-[rgba(123,211,255,0.35)] transition-colors card-elevated"
                                        data-testid={`services-card-${s.slug}`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="h-12 w-12 rounded-xl bg-[rgba(46,168,255,0.10)] border border-[rgba(46,168,255,0.25)] flex items-center justify-center">
                                                {Icon ? <Icon className="h-5 w-5 text-[#7BD3FF]" strokeWidth={1.6} /> : null}
                                            </div>
                                            <span className="font-mono text-xs text-[#8FA0B8]">{String(i + 1).padStart(2, "0")}</span>
                                        </div>
                                        <h3 className="mt-5 font-heading text-xl font-semibold text-white">{s.title}</h3>
                                        <p className="mt-2 text-sm text-[#C9D3E1] leading-relaxed">{s.short}</p>
                                        <div className="mt-5 inline-flex items-center gap-1 text-sm text-[#7BD3FF] group-hover:text-white transition-colors">
                                            Details ansehen <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <Stats />

            {/* Quick links to all main pages */}
            <section className="relative py-16 sm:py-20 border-t border-white/[0.06]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { to: "/ueber-uns", label: "Über uns" },
                            { to: "/ablauf", label: "Ablauf" },
                            { to: "/einsatzgebiet", label: "Einsatzgebiet" },
                            { to: "/faq", label: "FAQ" },
                        ].map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className="group rounded-xl border border-white/[0.10] bg-[rgba(18,27,42,0.4)] hover:bg-[rgba(46,168,255,0.06)] hover:border-[rgba(123,211,255,0.35)] p-5 transition-colors"
                            >
                                <div className="flex items-center justify-between text-white">
                                    <span className="font-heading">{item.label}</span>
                                    <ArrowRight className="h-4 w-4 text-[#7BD3FF] transition-transform group-hover:translate-x-0.5" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
